'use client';

import { c as _c } from "react/compiler-runtime";
import { Suspense } from 'react';
import { useRouter } from '../../utils/hooks/useRouter.ts';
import { Link, type LinkProps } from './link.tsx';

/**
 * Resolves the current pathname and sets aria-current on the link
 * when it matches. Rendered inside Suspense so the pathname lookup
 * doesn't block the static shell during prerender.
 */
const NavLinkWithPathname: React.FC<LinkProps> = props => {
  const {
    usePathname
  } = useRouter();
  const pathname = usePathname();
  const isCurrent = pathname === props.href;
  return <Link {...props} {...isCurrent && {
    'aria-current': 'page' as const
  }} />;
};

/**
 * A navigation-aware link that sets `aria-current="page"` when the link's
 * href matches the current pathname. Use this in navs (header, footer,
 * sidebar) where active state indication matters.
 *
 * For body content links, use `<Link>` directly — it's lighter and doesn't
 * subscribe to pathname changes.
 */
export const NavLink: React.FC<LinkProps> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense fallback={<Link {...props} />}><NavLinkWithPathname {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};